import { useRef, useState, useEffect } from 'react';
import QuestionInput from './components/QuestionInput';
import PromptSuggestions from './components/PromptSuggestions';
import LoadingDots from './components/LoadingDots';
import PromptLibraryModal from './components/PromptLibraryModal'; // Import Modal
import AgentMarkdown, { CHAT_HUMAN_TEXT_CLASS, CHAT_STRUCTURE_TITLE_CLASS } from './components/AgentMarkdown';
import ReactMarkdown from 'react-markdown';
import { Pie, Bar, Line } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    ArcElement,
    BarElement,
    PointElement,
    LineElement,
    Tooltip,
    Legend,
} from 'chart.js';
import { normalizeChartData } from '../../utils/chartUtils';
import { getUserFriendlyErrorMessage } from '../../utils/errorMessages';
import chatApi from '../../api/chatApi';
import qboApi from '../../api/qboApi'; // Import qboApi
import { AnswerMeta, FeedbackReasonCode, FeedbackType, RefreshVariant } from '../../api/types';
// Added Sheet, FileText, CheckCircle2 for the new UI
import { File, Copy, ThumbsUp, ThumbsDown, Sheet, FileText, CheckCircle2, RefreshCw, Plus, LibraryBig, Library, Search, Database, ShieldCheck, Sparkles, Brain, LoaderCircle, ChevronRight, ChevronDown } from 'lucide-react';

ChartJS.register(
    CategoryScale,
    LinearScale,
    ArcElement,
    BarElement,
    PointElement,
    LineElement,
    Tooltip,
    Legend
);

// --- Types ---
interface Message {
    role: 'ai' | 'human';
    content: string;
    tables?: any[];
    charts?: any[];
    suggested_questions?: string[];
    activity?: ActivityItem[];
    message_id?: string;
    parent_user_message_id?: string;
    answer_meta?: AnswerMeta;
    revisions?: any[];
    latest_feedback?: any;
    feedback_state?: {
        submitted: boolean;
        feedback: FeedbackType;
        reason_code?: FeedbackReasonCode;
    };
}

type ActivityItem = {
    type: string;
    message: string;
    status: 'active' | 'done';
};

type ActivityLike = ActivityItem | string | { type?: string; message?: string; status?: 'active' | 'done' };

interface ChatResponseData {
    text: string;
    tables?: any[];
    charts?: any[];
    suggested_questions?: string[];
    meta?: AnswerMeta;
}

interface ChatProps {
    messages?: Message[];
    conversationId?: string;
    mode?: 'active' | 'history';
    readOnly?: boolean;
    storageNamespace?: string;
    historySource?: 'chat' | 'ledger_agent';
    continuationContext?: string;
}

// --- Helper for File Icons (Same as QuestionInput) ---
const getFileIconConfig = (fileName: string) => {
    const extension = fileName.split('.').pop()?.toLowerCase();
    if (['csv', 'xls', 'xlsx'].includes(extension || '')) {
        return { Icon: Sheet, color: 'text-green-600', bg: 'bg-green-50' };
    }
    return { Icon: FileText, color: 'text-red-600', bg: 'bg-red-50' };
};

const normalizeActivityItem = (item: ActivityLike): ActivityItem => {
    if (typeof item === 'string') {
        return {
            type: 'status',
            message: item,
            status: 'done',
        };
    }

    return {
        type: item.type || 'status',
        message: item.message || '',
        status: item.status === 'active' ? 'active' : 'done',
    };
};

const getActivityPresentation = (item: ActivityItem) => {
    const stepType = item.type || 'status';
    if (item.status === 'done') {
        return {
            Icon: CheckCircle2,
            iconClassName: 'text-gray-500',
        };
    }

    if (stepType.includes('supervisor')) {
        return {
            Icon: Search,
            iconClassName: 'text-gray-500',
        };
    }
    if (stepType.includes('memory')) {
        return {
            Icon: Brain,
            iconClassName: 'text-gray-500',
        };
    }
    if (stepType.includes('readiness')) {
        return {
            Icon: ShieldCheck,
            iconClassName: 'text-gray-500',
        };
    }
    if (stepType.includes('resolver') || stepType.includes('tool')) {
        return {
            Icon: Database,
            iconClassName: 'text-gray-500',
        };
    }
    return {
        Icon: Sparkles,
        iconClassName: 'text-gray-500',
    };
};

const appendActivityStep = (steps: ActivityItem[], event: { type: string; message: string }) => {
    const completed = steps.map((step) => ({ ...step, status: 'done' as const }));
    const last = completed[completed.length - 1];
    if (last && last.type === event.type && last.message === event.message) {
        return completed;
    }
    return [
        ...completed,
        {
            type: event.type,
            message: event.message,
            status: 'active' as const,
        },
    ];
};

const finalizeActivitySteps = (steps: ActivityItem[]) =>
    steps.map((step) => ({ ...step, status: 'done' as const }));

// --- Sub-Component for AI Message ---
const AiMessage: React.FC<{
    msg: Message;
    onSuggestionClick: (q: string) => void;
    onRecycleSuggestions: () => void;
    onFeedback: (feedback: FeedbackType, reasonCode?: FeedbackReasonCode, reasonNote?: string) => Promise<void>;
    onRefresh: (variant: RefreshVariant) => Promise<void>;
    readOnly?: boolean;
}> = ({ msg, onSuggestionClick, onRecycleSuggestions, onFeedback, onRefresh, readOnly }) => {
    const [copied, setCopied] = useState(false);
    const [showActivity, setShowActivity] = useState(false);
    const [showBadMenu, setShowBadMenu] = useState(false);
    const [showRefreshMenu, setShowRefreshMenu] = useState(false);
    const [reasonCode, setReasonCode] = useState<FeedbackReasonCode>('wrong_period');
    const [reasonNote, setReasonNote] = useState('');
    const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const activityWasActiveRef = useRef(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const activity = (msg.activity ?? []).map(normalizeActivityItem).filter((item) => item.message);
    const hasMessageId = Boolean(msg.message_id);
    const suggestedQuestions = (msg.suggested_questions || []).filter(Boolean).slice(0, 3);

    useEffect(() => {
        const hasActive = activity.some((item) => item.status === 'active');
        if (hasActive) {
            activityWasActiveRef.current = true;
            setShowActivity(true);
            return;
        }
        if (activity.length > 0 && activityWasActiveRef.current) {
            setShowActivity(false);
            activityWasActiveRef.current = false;
            return;
        }
        if (activity.length === 0) {
            activityWasActiveRef.current = false;
        }
    }, [activity]);

    useEffect(() => {
        if (!showActivity || activity.length === 0) return;
        containerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }, [showActivity, activity.length]);

    const handleCopy = () => {
        navigator.clipboard.writeText(msg.content);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const submitGoodFeedback = async () => {
        if (!hasMessageId || readOnly || isSubmittingFeedback) return;
        setIsSubmittingFeedback(true);
        try {
            await onFeedback('good');
        } finally {
            setIsSubmittingFeedback(false);
        }
    };

    const submitBadFeedback = async () => {
        if (!hasMessageId || readOnly || isSubmittingFeedback) return;
        setIsSubmittingFeedback(true);
        try {
            await onFeedback('bad', reasonCode, reasonCode === 'other' ? reasonNote : undefined);
            setShowBadMenu(false);
        } finally {
            setIsSubmittingFeedback(false);
        }
    };

    const runRefresh = async (variant: RefreshVariant) => {
        if (!hasMessageId || readOnly || isRefreshing) return;
        setIsRefreshing(true);
        try {
            await onRefresh(variant);
            setShowRefreshMenu(false);
        } finally {
            setIsRefreshing(false);
        }
    };

    return (
        <div ref={containerRef} className="w-full space-y-3 scroll-mb-32 text-sm text-gray-900">
            {activity.length > 0 && (
                <div className="rounded-lg border border-gray-200 bg-gray-50">
                    <button
                        type="button"
                        onClick={() => setShowActivity((prev) => !prev)}
                        className="flex w-full items-center justify-between px-3 py-2 text-left"
                    >
                        <div className="min-w-0">
                            <div className="text-[11px] font-medium uppercase tracking-[0.08em] text-gray-500">
                                Activity
                            </div>
                            <div className="mt-0.5 truncate text-sm text-gray-600">
                                {activity[activity.length - 1]?.message}
                            </div>
                        </div>
                        <div className="ml-3 flex items-center gap-2 text-xs text-gray-500">
                            <span>{activity.length} step{activity.length === 1 ? '' : 's'}</span>
                            {showActivity ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        </div>
                    </button>

                    {showActivity && (
                        <div className="border-t border-gray-200 px-3 py-2">
                            <div className="space-y-2">
                                {activity.map((step, index) => {
                                    const { Icon, iconClassName } = getActivityPresentation(step);
                                    const isLast = index === activity.length - 1;
                                    return (
                                        <div key={`${step.type}-${step.message}-${index}`} className="flex gap-3">
                                            <div className="flex flex-col items-center">
                                                <div className="flex h-6 w-6 items-center justify-center rounded-full border border-gray-200 bg-white">
                                                    {step.status === 'active' ? (
                                                        <LoaderCircle className={`h-3.5 w-3.5 animate-spin ${iconClassName}`} />
                                                    ) : (
                                                        <Icon className={`h-3.5 w-3.5 ${iconClassName}`} />
                                                    )}
                                                </div>
                                                {!isLast && <div className="mt-1 h-4 w-px bg-gray-200" />}
                                            </div>
                                            <div className="pt-0.5">
                                                <div className="text-sm text-gray-700">{step.message}</div>
                                                <div className="mt-0.5 text-xs text-gray-400">
                                                    {step.status === 'active' ? 'In progress' : 'Completed'}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>
            )}

            <AgentMarkdown content={msg.content} />

            {msg.tables?.map((table, index) => (
                <div key={`table-${index}`}>
                    {table.title && (
                        <h3 className={CHAT_STRUCTURE_TITLE_CLASS}>
                            <ReactMarkdown>{table.title}</ReactMarkdown>
                        </h3>
                    )}
                    <div className="overflow-x-auto">
                        <table className="w-full border-collapse border border-gray-300 text-sm">
                            <thead className="bg-gray-100">
                                <tr>
                                    {table.headers.map((header: string, i: number) => (
                                        <th key={i} className="border border-gray-300 px-3 py-2 text-left">
                                            <ReactMarkdown>{header}</ReactMarkdown>
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {table.rows.map((row: string[], rowIdx: number) => (
                                    <tr key={rowIdx}>
                                        {row.map((cell, colIdx) => (
                                            <td key={colIdx} className="border border-gray-300 px-3 py-2">
                                                {cell}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            ))}

            {msg.charts?.map((rawChart, index) => {
                const chart = normalizeChartData(rawChart);
                const ChartComponent = chart.type === 'bar' ? Bar : chart.type === 'line' ? Line : Pie;
                const isValid = chart?.data?.labels && Array.isArray(chart.data?.datasets);

                if (!isValid) return <div key={`chart-${index}`} className="text-red-500 text-sm">⚠️ Invalid chart data</div>;

                return (
                    <div key={`chart-${index}`} className="mt-4">
                        {chart.title && (
                            <h3 className={CHAT_STRUCTURE_TITLE_CLASS}>
                                <ReactMarkdown>{chart.title}</ReactMarkdown>
                            </h3>
                        )}
                        <div className="w-full max-w-lg mx-auto overflow-x-auto">
                            <ChartComponent data={chart.data} options={chart.options} />
                        </div>
                    </div>
                );
            })}

            {msg.content && (
                <div className="flex items-center gap-1 mt-4 pt-2 relative">
                    <button
                        onClick={handleCopy}
                        className="p-1.5 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors flex items-center gap-1.5 text-xs font-medium"
                        title="Copy to clipboard"
                    >
                        <Copy className="w-4 h-4" />
                        {copied && <span className="ml-1 text-green-600">Copied</span>}
                    </button>
                    <button
                        className="p-1.5 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors disabled:text-gray-300 disabled:hover:bg-transparent"
                        title={hasMessageId ? "Good response" : "Unavailable for legacy messages"}
                        disabled={!hasMessageId || !!readOnly || isSubmittingFeedback}
                        onClick={submitGoodFeedback}
                    >
                        <ThumbsUp className="w-4 h-4" />
                    </button>
                    <button
                        className="p-1.5 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors disabled:text-gray-300 disabled:hover:bg-transparent"
                        title={hasMessageId ? "Bad response" : "Unavailable for legacy messages"}
                        disabled={!hasMessageId || !!readOnly || isSubmittingFeedback}
                        onClick={() => {
                            setShowBadMenu((prev) => !prev);
                            setShowRefreshMenu(false);
                        }}
                    >
                        <ThumbsDown className="w-4 h-4" />
                    </button>
                    <button
                        onClick={() => {
                            setShowRefreshMenu((prev) => !prev);
                            setShowBadMenu(false);
                        }}
                        className="p-1.5 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors disabled:text-gray-300 disabled:hover:bg-transparent"
                        title={hasMessageId ? "Refresh answer" : "Unavailable for legacy messages"}
                        disabled={!hasMessageId || !!readOnly || isRefreshing}
                    >
                        <RefreshCw className="w-4 h-4" />
                    </button>
                    <button
                        onClick={onRecycleSuggestions}
                        className="p-1.5 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
                        title="Get new suggestions"
                    >
                        <Library className="w-4 h-4" />
                    </button>

                    {msg.feedback_state?.submitted && (
                        <span className="ml-2 text-xs text-gray-500">
                            Feedback submitted
                        </span>
                    )}

                    {showBadMenu && hasMessageId && !readOnly && (
                        <div className="absolute left-0 top-9 z-20 w-72 rounded-lg border border-gray-200 bg-white p-3 shadow-lg">
                            <div className="text-xs font-semibold text-gray-700 mb-2">Why was this bad?</div>
                            <select
                                value={reasonCode}
                                onChange={(e) => setReasonCode(e.target.value as FeedbackReasonCode)}
                                className="w-full rounded border border-gray-300 px-2 py-1 text-sm mb-2"
                            >
                                <option value="wrong_period">Wrong period</option>
                                <option value="wrong_entity">Wrong entity</option>
                                <option value="too_generic">Too generic</option>
                                <option value="hallucinated">Hallucinated</option>
                                <option value="other">Other</option>
                            </select>
                            {reasonCode === 'other' && (
                                <textarea
                                    value={reasonNote}
                                    onChange={(e) => setReasonNote(e.target.value)}
                                    className="w-full rounded border border-gray-300 px-2 py-1 text-sm mb-2"
                                    rows={2}
                                    placeholder="Optional note"
                                    maxLength={500}
                                />
                            )}
                            <div className="flex justify-end gap-2">
                                <button
                                    className="text-xs px-2 py-1 rounded border border-gray-300 hover:bg-gray-100"
                                    onClick={() => setShowBadMenu(false)}
                                    type="button"
                                >
                                    Cancel
                                </button>
                                <button
                                    className="text-xs px-2 py-1 rounded bg-gray-900 text-white hover:bg-gray-800 disabled:bg-gray-400"
                                    disabled={isSubmittingFeedback}
                                    onClick={submitBadFeedback}
                                    type="button"
                                >
                                    Submit
                                </button>
                            </div>
                        </div>
                    )}

                    {showRefreshMenu && hasMessageId && !readOnly && (
                        <div className="absolute left-24 top-9 z-20 w-44 rounded-lg border border-gray-200 bg-white p-2 shadow-lg">
                            <button className="w-full text-left text-sm px-2 py-1 rounded hover:bg-gray-100" onClick={() => runRefresh('try_again')} type="button">
                                Try again
                            </button>
                            <button className="w-full text-left text-sm px-2 py-1 rounded hover:bg-gray-100" onClick={() => runRefresh('longer')} type="button">
                                Longer
                            </button>
                            <button className="w-full text-left text-sm px-2 py-1 rounded hover:bg-gray-100" onClick={() => runRefresh('shorter')} type="button">
                                Shorter
                            </button>
                        </div>
                    )}
                </div>
            )}

            {!readOnly && suggestedQuestions.length > 0 && (
                <div className="mt-2 space-y-2">
                    {suggestedQuestions.map((question, index) => (
                        <button
                            key={`${question}-${index}`}
                            type="button"
                            onClick={() => onSuggestionClick(question)}
                            className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-left text-sm text-gray-700 transition hover:border-gray-300 hover:bg-gray-50"
                        >
                            {question}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

// --- Main Chat Component ---
const Chat: React.FC<ChatProps> = ({
    messages: externalMessages,
    conversationId,
    mode = 'active',
    readOnly = false,
    storageNamespace = 'default',
    historySource,
    continuationContext,
}) => {
    const conversationStorageKey = storageNamespace === 'default' ? 'conversation_id' : `${storageNamespace}_conversation_id`;
    const messagesStorageKey = storageNamespace === 'default' ? 'chat_messages' : `${storageNamespace}_chat_messages`;
    const [messages, setMessages] = useState<Message[]>(() => {
        if (mode === 'history') return externalMessages || [];
        const saved = sessionStorage.getItem(messagesStorageKey);
        return saved ? JSON.parse(saved) : (externalMessages || []);
    });

    const [currConversationId, setCurrConversationId] = useState(() => {
        if (mode === 'history') return conversationId || null;
        return sessionStorage.getItem(conversationStorageKey) || conversationId || null;
    });

    const [hasStarted, setHasStarted] = useState(() => messages.length > 0);
    const [isLoading, setIsLoading] = useState(false);
    const [showPromptLibrary, setShowPromptLibrary] = useState(false);

    const chatEndRef = useRef<HTMLDivElement>(null);
    const latestMessage = messages[messages.length - 1];
    const shouldShowLoadingDots = Boolean(
        isLoading &&
        (!latestMessage ||
            latestMessage.role !== 'ai' ||
            ((latestMessage.activity?.length || 0) === 0 && !latestMessage.content))
    );

    useEffect(() => {
        if (hasStarted && chatEndRef.current) {
            chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [messages, hasStarted, isLoading]);

    useEffect(() => {
        if (mode === 'active') {
            sessionStorage.setItem(messagesStorageKey, JSON.stringify(messages));
        }
    }, [messages, messagesStorageKey, mode]);

    useEffect(() => {
        if (mode === 'active' && currConversationId) {
            sessionStorage.setItem(conversationStorageKey, currConversationId);
        }
    }, [conversationStorageKey, currConversationId, mode]);

    useEffect(() => {
        if (externalMessages) {
            const normalized = externalMessages.map((msg) => {
                if (typeof msg.content === 'string') {
                    // 1. Try Markdown Code Block
                    const jsonMatch = msg.content.match(/```json\n?([\s\S]*?)```/);
                    let contentToParse = jsonMatch ? jsonMatch[1] : null;

                    // 2. Fallback: Try Raw JSON if it looks like an object
                    if (!contentToParse && msg.content.trim().startsWith('{') && msg.content.trim().endsWith('}')) {
                        contentToParse = msg.content;
                    }

                    if (contentToParse) {
                        try {
                            const parsed = JSON.parse(contentToParse);
                            return {
                                role: msg.role as 'ai' | 'human',
                                content: parsed.text || '',
                                tables: parsed.tables || [],
                                charts: parsed.charts || [],
                                suggested_questions: parsed.suggested_questions || [],
                                answer_meta: parsed.meta || msg.answer_meta,
                                message_id: msg.message_id || parsed.meta?.message_id,
                                parent_user_message_id: msg.parent_user_message_id || parsed.meta?.parent_user_message_id,
                                latest_feedback: msg.latest_feedback,
                            };
                        } catch (e) {
                            console.warn("Failed to parse embedded JSON content:", e);
                        }
                    }
                    return {
                        role: msg.role as 'ai' | 'human',
                        content: msg.content,
                        message_id: msg.message_id,
                        parent_user_message_id: msg.parent_user_message_id,
                        answer_meta: msg.answer_meta,
                        latest_feedback: msg.latest_feedback,
                    };
                }
                return {
                    role: msg.role as 'ai' | 'human',
                    content: (msg.content as any).text || '',
                    tables: (msg.content as any).tables || [],
                    charts: (msg.content as any).charts || [],
                    suggested_questions: (msg.content as any).suggested_questions || [],
                    answer_meta: (msg.content as any).meta || msg.answer_meta,
                    message_id: msg.message_id || (msg.content as any).meta?.message_id,
                    parent_user_message_id: msg.parent_user_message_id || (msg.content as any).meta?.parent_user_message_id,
                    latest_feedback: msg.latest_feedback,
                };
            });
            setMessages(normalized);
        }
    }, [externalMessages]);

    const isMongoObjectId = (value: string | null | undefined) => /^[a-f0-9]{24}$/i.test(String(value || '').trim());

    const buildLedgerHistoryContinuationPrompt = (question: string) => {
        const trimmedQuestion = question.trim();
        const trimmedContext = String(continuationContext || '').trim();
        if (!trimmedContext) return trimmedQuestion;
        return [
            'Use this ledger agent conversation as background context. Continue the discussion naturally in normal chat mode.',
            'Ledger agent context:',
            trimmedContext,
            '',
            `User question: ${trimmedQuestion}`,
        ].join('\n');
    };

    const handleRecycleSuggestions = async (msgIndex: number) => {
        if (readOnly) return;
        try {
            const data = await qboApi.getSampleQuestions();
            const allQuestions = data.sample_questions || [];

            // Randomly select 3 unique questions
            const shuffled = [...allQuestions].sort(() => 0.5 - Math.random());
            const selected = shuffled.slice(0, 3).map(q => q.question);

            setMessages((prev) => {
                const updated = [...prev];
                if (updated[msgIndex]) {
                    updated[msgIndex] = {
                        ...updated[msgIndex],
                        suggested_questions: selected
                    };
                }
                return updated;
            });
        } catch (error) {
            console.error("Failed to recycle suggestions:", error);
        }
    };

    const handleNewChat = () => {
        setMessages([]);
        setCurrConversationId(null);
        // setHasStarted(false); // Removed to keep the "Active" layout (Header + Bottom Input)
        setHasStarted(true);     // Explicitly ensure we stay in active mode
        sessionStorage.removeItem("chat_messages");
        sessionStorage.removeItem("conversation_id");
    };

    const handleSendMessage = async (message: string) => {
        if (!message.trim() || readOnly) return;
        setHasStarted(true);
        const shouldForkLedgerHistoryToChat =
            mode === 'history' &&
            historySource === 'ledger_agent' &&
            !isMongoObjectId(currConversationId);
        const requestConversationId = shouldForkLedgerHistoryToChat ? null : (currConversationId || null);
        const requestQuestion = shouldForkLedgerHistoryToChat
            ? buildLedgerHistoryContinuationPrompt(message)
            : message;

        setMessages((prev) => {
            const cleanHistory = prev.map(msg => ({
                ...msg,
                suggested_questions: undefined
            }));

            return [
                ...cleanHistory,
                { role: 'human', content: message },
                { role: 'ai', content: '' }
            ];
        });

        setIsLoading(true);

        try {
            console.log("uploading msg to: ", requestConversationId);
            let streamedText = '';
            const data = await chatApi.askStream(
                requestQuestion,
                requestConversationId,
                undefined,
                {
                    onStatus: (event) => {
                        setMessages((prev) => {
                            const updated = [...prev];
                            const currentActivity = updated[updated.length - 1]?.activity || [];
                            const nextActivity = appendActivityStep(currentActivity, event);
                            updated[updated.length - 1] = {
                                role: 'ai',
                                content: streamedText,
                                tables: [],
                                charts: [],
                                suggested_questions: [],
                                activity: nextActivity,
                            };
                            return updated;
                        });
                    },
                    onToken: (delta) => {
                        streamedText += delta;
                        setMessages((prev) => {
                            const updated = [...prev];
                            updated[updated.length - 1] = {
                                role: 'ai',
                                content: streamedText,
                                tables: [],
                                charts: [],
                                suggested_questions: [],
                                activity: updated[updated.length - 1]?.activity || [],
                            };
                            return updated;
                        });
                    },
                }
            );

            if (data.conversation_id) {
                setCurrConversationId(data.conversation_id);
            }

            const responseData = data.response;
            let text = '', tables: any[] = [], charts: any[] = [], suggestedQs: string[] = [];
            let responseMeta: AnswerMeta | undefined;
            let messageId: string | undefined;
            let parentUserMessageId: string | undefined;

            if (typeof responseData === 'string') {
                text = responseData;
            } else if (typeof responseData === 'object') {
                const typedResponse = responseData as ChatResponseData;
                text = typedResponse.text || streamedText || '';
                tables = typedResponse.tables || [];
                charts = typedResponse.charts || [];
                suggestedQs = typedResponse.suggested_questions || [];
                responseMeta = typedResponse.meta;
                messageId = typedResponse.meta?.message_id;
                parentUserMessageId = typedResponse.meta?.parent_user_message_id;
            }

            setMessages((prev) => {
                const updated = [...prev];
                updated[updated.length - 1] = {
                    role: 'ai',
                    content: text,
                    tables,
                    charts,
                    suggested_questions: suggestedQs,
                    answer_meta: responseMeta,
                    message_id: messageId,
                    parent_user_message_id: parentUserMessageId,
                    activity: finalizeActivitySteps(updated[updated.length - 1]?.activity || []),
                };
                return updated;
            });
        } catch (error: any) {
            console.error("Chat error:", error.message);
        } finally {
            setIsLoading(false);
        }
    };

    const findRefreshQuestion = (msgIndex: number): string => {
        const msg = messages[msgIndex];
        const parentId = msg?.answer_meta?.parent_user_message_id || msg?.parent_user_message_id;
        if (parentId) {
            const parentMsg = messages.find((m) => m.role === 'human' && m.message_id === parentId);
            if (parentMsg?.content) return parentMsg.content;
        }
        for (let i = msgIndex - 1; i >= 0; i -= 1) {
            if (messages[i]?.role === 'human') return messages[i].content;
        }
        return '';
    };

    const handleSubmitFeedback = async (
        msgIndex: number,
        feedback: FeedbackType,
        reasonCode?: FeedbackReasonCode,
        reasonNote?: string,
    ) => {
        const msg = messages[msgIndex];
        if (!msg?.message_id || !currConversationId) return;
        try {
            await chatApi.submitAnswerFeedback(
                {
                    conversation_id: currConversationId,
                    answer_message_id: msg.message_id,
                    feedback,
                    reason_code: reasonCode,
                    reason_note: reasonNote,
                    answer_meta: msg.answer_meta as Record<string, unknown>,
                },
            );
            setMessages((prev) => {
                const updated = [...prev];
                if (!updated[msgIndex]) return prev;
                updated[msgIndex] = {
                    ...updated[msgIndex],
                    feedback_state: {
                        submitted: true,
                        feedback,
                        reason_code: reasonCode,
                    },
                };
                return updated;
            });
        } catch (error) {
            console.error("Failed to submit feedback:", error);
        }
    };

    const handleRefreshAnswer = async (msgIndex: number, variant: RefreshVariant) => {
        const msg = messages[msgIndex];
        if (!msg?.message_id || !currConversationId) return;
        const question = findRefreshQuestion(msgIndex);
        if (!question) return;

        const today = new Date();
        const year = today.getFullYear();
        const month = today.getMonth() + 1;

        let streamedText = '';
        const targetMessageId = msg.message_id;
        const updateByMessageId = (updater: (current: Message) => Message) => {
            setMessages((prev) =>
                prev.map((item) => (item.message_id === targetMessageId ? updater(item) : item))
            );
        };

        try {
            const payload = await chatApi.refreshAnswerStream(
                currConversationId,
                targetMessageId,
                question,
                'chat',
                year,
                month,
                variant,
                (msg.answer_meta as Record<string, unknown>) || undefined,
                undefined,
                {
                    onStatus: (event) => {
                        updateByMessageId((current) => ({
                            ...current,
                            content: streamedText || current.content,
                            activity: appendActivityStep(current.activity || [], event),
                        }));
                    },
                    onToken: (delta) => {
                        streamedText += delta;
                        updateByMessageId((current) => ({
                            ...current,
                            content: streamedText,
                            activity: current.activity || [],
                        }));
                    },
                },
            );
            const responseData = payload.response as ChatResponseData;
            updateByMessageId((current) => ({
                ...current,
                content: responseData?.text || streamedText || current.content,
                tables: responseData?.tables || [],
                charts: responseData?.charts || [],
                suggested_questions: responseData?.suggested_questions || [],
                answer_meta: responseData?.meta,
                message_id: responseData?.meta?.message_id || current.message_id,
                parent_user_message_id: responseData?.meta?.parent_user_message_id || current.parent_user_message_id,
                activity: finalizeActivitySteps(current.activity || []),
            }));
        } catch (error) {
            console.error("Failed to refresh answer:", error);
        }
    };

    const handleFileUpload = async (file: File) => {
        try {
            const isNew = !hasStarted;
            const res = await chatApi.uploadFile(file, isNew ? null : currConversationId);

            if (res.conversation_id) {
                setCurrConversationId(res.conversation_id);
            }

            if (isNew) setHasStarted(true);

            const fileMessage: Message = {
                role: 'human',
                content: "FILE: " + file.name,
            };
            setMessages((prev) => [...prev, fileMessage]);

            alert(`File uploaded successfully to ID: ${res.conversation_id}`);
        } catch (err: any) {
            console.error("File upload failed:", err);
            alert(getUserFriendlyErrorMessage(err, 'Failed to upload file. Please try again.'));
        }
    };

    const handlePromptSelect = (prompt: string) => {
        setShowPromptLibrary(false);
        handleSendMessage(prompt);
    };

    return (
        <div className="main-section-container flex-col h-full text-black relative px-2">
            {showPromptLibrary && (
                <PromptLibraryModal
                    onClose={() => setShowPromptLibrary(false)}
                    onSelect={handlePromptSelect}
                />
            )}

            {/* CHAT HEADER - Render when hasStarted (i.e., when messages exist) */}
            {hasStarted && !readOnly && (
                <div
                    className="w-full h-[37px] border-b-2 border-gray-100 flex justify-end items-center gap-6 px-6 py-8 shrink-0"
                    style={{
                        // width: 1192 explicitly requested, but 'w-full' is safer for responsiveness, 
                        // unless explicitly fixed width is mandatory. Use w-full to fill container.
                        // height: '37px',
                        // gap: '24px',
                        // borderBottomWidth: '2px'
                    }}
                >
                    <button
                        onClick={() => setShowPromptLibrary(true)}
                        className="py-2 px-4 border rounded-lg border-gray-400 bg-gray-50 hover:bg-gray-100 flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors"
                    >
                        Prompt Library
                        <LibraryBig className="w-5 h-5" />
                    </button>
                    <button
                        onClick={handleNewChat}
                        className="py-2 px-4 border rounded-lg border-gray-900  bg-gray-900 hover:bg-gray-800 flex items-center gap-2 text-sm font-medium text-white hover:text-gray-100 transition-colors"
                    >
                        New Chat
                        <Plus className="w-5 h-5" />
                    </button>
                </div>
            )}

            {hasStarted ? (
                <div className="w-full h-[calc(100%-37px)] overflow-y-auto text-sm text-gray-900">
                    <div className="min-h-full flex flex-col">

                        {/* MESSAGES */}
                        <div className="flex-1 w-full max-w-[820px] mx-auto p-4 sm:p-0">
                            {messages.map((msg, idx) => (
                                <div key={idx} className="py-4">
                                    {msg.role === 'human' ? (
                                        msg.content.startsWith("FILE: ") ? (

                                            // --- NEW FILE MESSAGE UI ---
                                            <div className="flex w-full justify-end">
                                                {/* Replaced old simple box with the detailed card design */}
                                                <div className="w-full max-w-[200px] border border-gray-200 rounded-lg p-3 flex items-center gap-3 bg-white shadow-sm">

                                                    {/* 1. Dynamic Icon */}
                                                    {(() => {
                                                        const fileName = msg.content.replace("FILE: ", "");
                                                        const { Icon, color, bg } = getFileIconConfig(fileName);
                                                        return (
                                                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${bg}`}>
                                                                <Icon className={`w-6 h-6 ${color}`} />
                                                            </div>
                                                        );
                                                    })()}

                                                    {/* 2. File Info */}
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-sm font-medium text-gray-900 truncate mb-1">
                                                            {msg.content.replace("FILE: ", "")}
                                                        </p>

                                                        <div className="flex items-center gap-2 text-xs text-gray-500">
                                                            <span className="flex items-center gap-1 text-green-600 font-medium">
                                                                <CheckCircle2 className="w-3 h-3" /> Completed
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                        ) : (
                                            // Normal Human Message
                                            <div className="flex w-full justify-end">
                                                <div className={`p-4 w-fit bg-gray-100 rounded-lg ${CHAT_HUMAN_TEXT_CLASS}`}>
                                                    {msg.content}
                                                </div>
                                            </div>
                                        )
                                    ) : (
                                        <AiMessage
                                            msg={msg}
                                            onSuggestionClick={handleSendMessage}
                                            onRecycleSuggestions={() => handleRecycleSuggestions(idx)}
                                            onFeedback={(feedback, reasonCode, reasonNote) =>
                                                handleSubmitFeedback(idx, feedback, reasonCode, reasonNote)
                                            }
                                            onRefresh={(variant) => handleRefreshAnswer(idx, variant)}
                                            readOnly={readOnly}
                                        />
                                    )}
                                </div>
                            ))}

                            {shouldShowLoadingDots && (
                                <div className="py-1">
                                    <div className="w-full max-w-[350px] sm:max-w-full space-y-3">
                                        <LoadingDots />
                                    </div>
                                </div>
                            )}

                            <div ref={chatEndRef} />
                        </div>

                        {/* STICKY INPUT FOOTER */}
                        {!readOnly && (
                            <div className="sticky bottom-0 w-full bg-white pb-5 pt-2 z-10">
                                <div className="w-full max-w-[820px] mx-auto p-4 sm:p-0">
                                    <QuestionInput
                                        onSendMessage={handleSendMessage}
                                        onFileUpload={handleFileUpload}
                                        hasStarted={hasStarted}
                                    />
                                </div>
                            </div>
                        )}

                    </div>
                </div>
            ) : (
                <div className="h-full flex flex-col items-center justify-center px-6">
                    <div className="w-full max-w-[820px] flex flex-col gap-6">
                        <div className="text-center flex flex-col gap-2">
                            <h1 className="text-3xl font-semibold text-gray-900">
                                How can AI CFO help you today?
                            </h1>
                            <p className="text-gray-500 text-base">
                                Select a prompt below or enter your own question to get real-time financial insights.
                            </p>
                        </div>
                        {!readOnly && (
                            <>
                                <QuestionInput onSendMessage={handleSendMessage} onFileUpload={handleFileUpload} hasStarted={hasStarted} />
                                <PromptSuggestions onSelect={handlePromptSelect} />
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default Chat;
